/*
 * PublishSchemaTemplateModal — nimmt einen Device-Sub-Tree und meldet ihn
 * als wiederverwendbares Schema-Template beim Backend an. Der Server
 * schnappt sich Nodes + Leaves + (optional) Credentials und legt eine
 * versionierte Vorlage im Template-Katalog ab.
 *
 * Scope=Tenant: sichtbar für den aktuellen Mandanten. Scope=Global bleibt
 * V1 disabled (Nur-Admin, Backend-seitig sowieso permission-gated).
 *
 * Credentials-Checkbox: default true, weil der übliche Publish-Fall ein
 * Referenz-Device ist, das der Techniker frisch getestet und gespeichert
 * hat. Bei Scope=Global wird sie hart auf false gezwungen — Global darf
 * KEINE Credentials tragen.
 */

import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { HeroSubmitDiscovery, IconCancel, IconSave } from '../icons/HvacIcons';
import './hv-modal.css';

/** 0 = Tenant, 1 = Global. Matches TemplateScope enum server-side. */
type TemplateScope = 0 | 1;

interface PublishResponse {
  templateId: string;
  version: number;
}

interface Props {
  plantId: string;
  deviceId: string;
  deviceProtocol: string | null;
  onClose(): void;
  onPublished(templateId: string, version: number): void;
}

const NAME_MAX = 200;
const DESC_MAX = 2000;

export function PublishSchemaTemplateModal({
  plantId,
  deviceId,
  deviceProtocol,
  onClose,
  onPublished,
}: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<TemplateScope>(0);
  const [includeCredentials, setIncludeCredentials] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Global-Scope ist V1 nicht verfügbar — der Server wirft 403, hier
  // sperren wir die Radio-Option gleich in der UI.
  const globalDisabled = true;

  // Global-Templates dürfen keine Creds tragen; sobald jemand irgendwann
  // Global aktiviert (V2), snappen wir die Checkbox aus.
  useEffect(() => {
    if (scope === 1 && includeCredentials) setIncludeCredentials(false);
  }, [scope]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, saving]);

  const save = async () => {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setError('Name ist erforderlich.');
      return;
    }
    if (trimmedName.length > NAME_MAX) {
      setError(`Name darf höchstens ${NAME_MAX} Zeichen haben.`);
      return;
    }
    const trimmedDesc = description.trim();
    if (trimmedDesc.length > DESC_MAX) {
      setError(`Beschreibung darf höchstens ${DESC_MAX} Zeichen haben.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        name: trimmedName,
        scope,
        includeCredentials: scope === 1 ? false : includeCredentials,
      };
      if (trimmedDesc.length > 0) body.description = trimmedDesc;
      const res = await api.post<PublishResponse>(
        `/api/plants/${plantId}/devices/${deviceId}/publish-as-schema-template`,
        body,
      );
      onPublished(res.templateId, res.version);
      onClose();
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string; detail?: string })?.error
          ?? (e.body as { detail?: string })?.detail;
        if (e.status === 409) {
          setError(bodyMsg ?? 'Es gibt bereits ein Template mit diesem Namen für dieses Protokoll.');
        } else if (e.status === 403) {
          setError(bodyMsg ?? 'Keine Berechtigung, Templates zu veröffentlichen.');
        } else if (e.status === 404) {
          setError(bodyMsg ?? 'Device oder Anlage nicht gefunden.');
        } else if (e.status === 400) {
          setError(bodyMsg ?? 'Ungültige Eingabe.');
        } else {
          setError(bodyMsg ?? `Speichern fehlgeschlagen (${e.status}).`);
        }
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler.');
      }
      setSaving(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !saving && onClose()}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Als Schema-Template speichern</h2>
          <button
            type="button"
            class="hv-modal__close"
            onClick={onClose}
            aria-label="Schließen"
            disabled={saving}
          >×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__hero" style={{ color: 'var(--accent)' }}>
            <HeroSubmitDiscovery size={72} />
          </div>
          <p class="hv-modal__hint">
            Nodes + Leaves dieses Devices werden als wiederverwendbare Vorlage
            im Template-Katalog abgelegt. Protokoll:
            {' '}<code>{deviceProtocol ?? '(unbekannt)'}</code>.
          </p>
          <div class="hv-modal__field">
            <label>
              Name<span class="hv-modal__field-required">*</span>
            </label>
            <input
              type="text"
              value={name}
              maxLength={NAME_MAX}
              placeholder="z.B. Bösch WP-Manager Standard"
              onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
              disabled={saving}
            />
          </div>
          <div class="hv-modal__field">
            <label>Beschreibung</label>
            <textarea
              class="hv-modal__textarea"
              value={description}
              maxLength={DESC_MAX}
              rows={3}
              placeholder="optional — kurze Notiz für Kollegen"
              onInput={(e) => setDescription((e.currentTarget as HTMLTextAreaElement).value)}
              disabled={saving}
            />
          </div>
          <div class="hv-modal__field">
            <label>Sichtbarkeit</label>
            <div style={{ display: 'block', marginTop: '4px' }}>
              <label style={{ display: 'block', padding: '4px 0', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 'var(--fs-sm)' }}>
                <input
                  type="radio"
                  name="publish-scope"
                  checked={scope === 0}
                  onChange={() => setScope(0)}
                  disabled={saving}
                  style={{ marginRight: '8px', verticalAlign: 'middle' }}
                />
                <span style={{ verticalAlign: 'middle' }}>Nur unser Mandant (Tenant)</span>
              </label>
              <div style={{ margin: '0 0 8px 24px', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
                Vorlage ist nur im aktuellen Mandanten sichtbar.
              </div>
              <label style={{ display: 'block', padding: '4px 0', cursor: globalDisabled ? 'not-allowed' : 'pointer', color: 'var(--text-primary)', fontSize: 'var(--fs-sm)', opacity: globalDisabled ? 0.55 : 1 }}>
                <input
                  type="radio"
                  name="publish-scope"
                  checked={scope === 1}
                  onChange={() => setScope(1)}
                  disabled={saving || globalDisabled}
                  style={{ marginRight: '8px', verticalAlign: 'middle' }}
                />
                <span style={{ verticalAlign: 'middle' }}>Global (alle Mandanten)</span>
              </label>
              <div style={{ margin: '0 0 4px 24px', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
                Nur Sinnograte-Admin. V1 nicht verfügbar.
              </div>
            </div>
          </div>
          <div class="hv-modal__field">
            <label>Zugangsdaten</label>
            <label style={{ display: 'block', padding: '4px 0', cursor: scope === 1 ? 'not-allowed' : 'pointer', color: 'var(--text-primary)', fontSize: 'var(--fs-sm)', opacity: scope === 1 ? 0.55 : 1 }}>
              <input
                type="checkbox"
                checked={scope === 1 ? false : includeCredentials}
                onChange={(e) => setIncludeCredentials((e.currentTarget as HTMLInputElement).checked)}
                disabled={saving || scope === 1}
                style={{ marginRight: '8px', verticalAlign: 'middle' }}
              />
              <span style={{ verticalAlign: 'middle' }}>Zugangsdaten mit speichern</span>
            </label>
            <div style={{ margin: '0 0 4px 24px', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
              {scope === 1
                ? 'Global-Templates dürfen keine Credentials tragen.'
                : 'AES-GCM verschlüsselt; nur für unseren Mandanten sichtbar.'}
            </div>
          </div>
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          <div class="hv-modal__footer-spacer" />
          <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button
            type="button"
            class="hv-btn hv-btn--primary"
            onClick={save}
            disabled={saving || name.trim().length === 0}
          >
            {saving ? 'Speichere…' : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconSave size={14} />Speichern</span>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
